import React, { useEffect, useState } from "react";
import { Loader2, Save, PlugZap, UploadCloud, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, Field, inputCls, btnDark, btnLight, fmtWhen } from "./atoms";

const PROD_HUB = "https://curatedtrips.espiritutravel.com";

export function HubNativeCard({ settings, reload }) {
  const [url, setUrl] = useState(settings.hub_base_url || "");
  const [key, setKey] = useState("");
  const [enabled, setEnabled] = useState(!!settings.hub_enabled);
  const [busy, setBusy] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [pushResult, setPushResult] = useState(null);

  useEffect(() => { setUrl(settings.hub_base_url || ""); setEnabled(!!settings.hub_enabled); setKey(""); }, [settings]);

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put("/settings/hub/native", { hub_base_url: url, hub_api_key: key, hub_enabled: enabled });
      toast.success(data.hub_enabled ? "Conexión con el hub activa" : "Guardado (conexión desactivada)");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setBusy(null); }
  };

  const test = async () => {
    setBusy("test"); setTestResult(null);
    try {
      const { data } = await api.post("/settings/hub/native/test");
      setTestResult(data);
      data.ok ? toast.success(`El hub respondió ${data.status_code}`) : toast.error(`Fallo: ${data.status_code || data.error}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo probar"); }
    finally { setBusy(null); }
  };

  const pushAll = async () => {
    setBusy("push"); setPushResult(null);
    try {
      const { data } = await api.post("/settings/hub/native/push-all");
      setPushResult(data);
      toast[data.ok === data.sent ? "success" : "warning"](`${data.ok}/${data.sent} viajes aceptados por el hub`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo enviar"); }
    finally { setBusy(null); }
  };

  const configured = settings.hub_base_url && settings.hub_api_key_set;

  return (
    <Card kicker="Landing hub" title="Conexión con curatedtrips.espiritutravel.com" testid="hub-native-card">
      <p className="text-sm text-clay-700 -mt-2">
        El hub lee cada viaje de nuestro enlace público y lo pinta con su propio diseño. Aquí solo le <strong>avisamos al instante</strong>
        cuando publicas, cierras, cambias precio/fechas/cupo o cambian las plazas, para que lo vuelva a leer sin esperar a su
        sincronización horaria. Pega la URL del hub y la <em>API key de webhooks</em> que muestra su panel de administración.
      </p>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="URL del hub" hint={`Producción: ${PROD_HUB}. Mientras pruebas, su URL de preview.`}>
          <input data-testid="hub-native-url-input" className={inputCls} placeholder={PROD_HUB} value={url} onChange={(e) => setUrl(e.target.value)}/>
        </Field>
        <Field label="API key de webhooks del hub" hint={settings.hub_api_key_set ? `Guardada (${settings.hub_api_key_hint}). Deja vacío para conservarla.` : "Panel admin del hub → Webhook / API key."}>
          <input data-testid="hub-native-key-input" className={inputCls} type="password" autoComplete="off" placeholder={settings.hub_api_key_set ? "•••••••••••• (sin cambios)" : "clave del hub"} value={key} onChange={(e) => setKey(e.target.value)}/>
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="hub-native-enabled-toggle" className="accent-terracotta"/>
        Avisar al hub automáticamente en cada cambio
      </label>
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={save} disabled={busy === "save"} className={btnDark} data-testid="hub-native-save-btn">
          {busy === "save" ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Guardar conexión
        </button>
        <button onClick={test} disabled={!configured || busy === "test"} className={btnLight} data-testid="hub-native-test-btn">
          {busy === "test" ? <Loader2 size={12} className="animate-spin"/> : <PlugZap size={12}/>} Probar conexión
        </button>
        <button onClick={pushAll} disabled={!configured || busy === "push"} className={btnLight} data-testid="hub-native-push-btn" title="Envía al hub todos los viajes publicados y cerrados">
          {busy === "push" ? <Loader2 size={12} className="animate-spin"/> : <UploadCloud size={12}/>} Enviar todos los viajes al hub
        </button>
      </div>
      <div className="text-[12px] text-clay-600" data-testid="hub-native-status">
        {configured ? <span className={settings.hub_enabled ? "text-emerald-700 font-medium" : ""}>{settings.hub_enabled ? "Conectado · avisos automáticos activos" : "Configurado · avisos automáticos desactivados"}</span> : "Sin configurar"}
        {settings.hub_last_test && <> · última prueba {fmtWhen(settings.hub_last_test.at)}: {settings.hub_last_test.ok ? "OK" : `fallo ${settings.hub_last_test.status_code ?? ""}`}</>}
      </div>
      {testResult && (
        <div data-testid="hub-native-test-result" className={`text-[12px] p-3 border ${testResult.ok ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-terracotta/40 bg-terracotta/5 text-clay-900"}`}>
          {testResult.ok ? "✓" : "✗"} POST {testResult.url} → {testResult.status_code ?? "sin respuesta"} · {testResult.duration_ms} ms
          {testResult.error && <div className="mt-1 font-mono break-all">{testResult.error}</div>}
        </div>
      )}
      {pushResult && (
        <div className="border border-clay-200 p-3 text-[12px]" data-testid="hub-native-push-result">
          <div className="font-medium mb-1">{pushResult.ok}/{pushResult.sent} viajes aceptados</div>
          <ul className="space-y-0.5">
            {pushResult.results.map((r) => (
              <li key={r.curated_trip_id} className="flex items-center gap-2">
                {r.ok ? <CheckCircle2 size={12} className="text-emerald-600"/> : <XCircle size={12} className="text-terracotta"/>}
                <span className="text-clay-800">{r.name}</span>
                <span className="text-clay-500">· {r.status} · {r.status_code ?? "sin respuesta"}</span>
                {r.error && <span className="font-mono text-clay-500 truncate max-w-xs">{r.error}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
